import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, User, Building2 } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS } from "@/data/site";

function MobileMenu() {
  return (
    <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-teh-navy/10">
      <div className="teh-container py-6 flex flex-col gap-1">
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
            className={({ isActive }) =>
              `py-3 text-base font-medium border-b border-teh-navy/5 ${
                isActive ? "text-teh-green" : "text-teh-navy"
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
        <Link
          to="/group"
          data-testid="nav-mobile-group-signin"
          className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 border border-teh-navy/20 text-teh-navy text-sm font-medium rounded-full"
        >
          <Building2 className="w-4 h-4" />
          Facility sign in
        </Link>
        <Link
          to="/patient"
          data-testid="nav-mobile-patient-signin"
          className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 border border-teh-navy/20 text-teh-navy text-sm font-medium rounded-full"
        >
          <User className="w-4 h-4" />
          Patient sign in
        </Link>
        <Link
          to="/contact"
          className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 bg-teh-navy text-white text-sm font-medium rounded-full"
        >
          Refer a Patient
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden lg:flex items-center gap-10">
      {NAV_LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          data-testid={`nav-link-${l.label.toLowerCase()}`}
          className={({ isActive }) =>
            `text-sm font-medium tracking-wide transition-colors ${
              isActive ? "text-teh-green" : "text-teh-navy hover:text-teh-green"
            }`
          }
        >
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 bg-white transition-all duration-500 ${
        scrolled ? "border-b border-teh-navy/10 shadow-sm" : ""
      }`}
    >
      <div className="teh-container flex items-center justify-between h-32">
        <Logo />
        <DesktopNav />

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/group"
            data-testid="nav-group-signin"
            className="inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-teh-navy hover:text-teh-green transition"
          >
            <Building2 className="w-4 h-4" strokeWidth={1.8} />
            Facility sign in
          </Link>
          <Link
            to="/patient"
            data-testid="nav-patient-signin"
            className="inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-teh-navy hover:text-teh-green transition"
          >
            <User className="w-4 h-4" strokeWidth={1.8} />
            Patient sign in
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 px-5 py-3 bg-teh-navy text-white text-sm font-medium rounded-full hover:bg-teh-navyDark transition-all duration-300 hover:gap-3"
          >
            Refer a Patient
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-teh-navy"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && <MobileMenu />}
    </header>
  );
}
